import React, { useEffect, useState } from 'react';

import Helmet from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { pick } from 'lodash';
import jwtDecode from 'jwt-decode';
import { Box, Checkbox, InputAdornment, Link, Typography } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { authModel } from 'models';
import { ROUTES } from 'routes/constants';
import { getQueryParams } from 'utils';
import { CATEGORY_NAMES, METRIC_EVENT_NAMES } from 'metrics/constants';
import { useMatomo } from 'contexts/MatomoContext';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import {
  BuilderAuthCard,
  BuilderStyledButton,
  BuilderSubtitle,
  BuilderTextValidator,
  BuilderTitle,
  CheckboxHint,
  BuilderFormLink,
} from 'pages/auth/style';
import { AUTH_HEADERS } from 'models/constants';
import useIsMobile from 'hooks/useIsMobile';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { COLORS } from 'theme/constants';
import { LINKS } from 'components/constants';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';

import { BuilderContactUsFooter } from '../BuilderContactUsFooter';
import { BuilderAuthMessage } from '../BuilderAuthMessage/BuilderAuthMessage';

import { ReactComponent as CozmosSVGWhite } from 'assets/img/cozmos-logo.svg';
import { ReactComponent as GoogleIcon } from 'assets/img/google.svg';

interface Props {
  google: boolean;
}

function getAuthToken() {
  return getQueryParams().pre_auth_token as string;
}

interface DecodedTokenProps {
  email: string;
}

const BuilderSignUp = ({ google }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleServerError } = useServerErrorActions();
  const { trackEvent } = useMatomo();
  const { withPageProgressHandler } = useBusyProgressActions();
  const isMobile = useIsMobile();
  const { space_name: spaceName = '' } = getQueryParams();
  const [state, setState] = useState({
    clientName: String(spaceName),
    email: '',
    advertiseNotifications: false,
    password: '',
    passwordConfirmation: '',
  });
  const [emailToken, setEmailToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const preAuthToken = getAuthToken();
    if (!preAuthToken) return;
    if (google) {
      const decodedToken: DecodedTokenProps = jwtDecode(preAuthToken);

      setState(prevState => ({
        ...prevState,
        email: decodedToken.email ?? '',
      }));
    } else
      history.push({
        pathname: ROUTES.authGoogleSignUp,
        search: `?pre_auth_token=${preAuthToken}`,
      });
  }, []);

  if (emailToken !== undefined) {
    const onResendEmail = () => {
      authModel.resendEmail({ token: emailToken }).catch(error => {
        handleServerError(error);
      });
    };
    return (
      <BuilderAuthMessage
        tabTitle={t('pages.auth.signUpPage.tabTitle')}
        title={t('pages.auth.signUpPage.emailSentTitle')}
        text={<Trans i18nKey="pages.auth.signUpPage.emailSentMessage" values={{ email: state.email }} />}
        buttonText={t('pages.auth.signUpPage.resendEmailBtn')}
        onButtonClick={onResendEmail}
      />
    );
  }

  const signUp = withPageProgressHandler(() => {
    if (google) {
      setLoading(true);
      trackEvent({
        category: CATEGORY_NAMES.PUBTOOL_EVENTS,
        action: 'click',
        name: METRIC_EVENT_NAMES.SIGN_UP_GOOGLE,
      });
      return authModel
        .socialSignUp({
          ...state,
          ...pick(state, [authModel.fields.space, authModel.fields.password, authModel.fields.passwordConfirmation]),
          token: getAuthToken(),
        })
        .then(data => {
          trackEvent({
            category: CATEGORY_NAMES.PUBTOOL_EVENTS,
            action: 'signup',
            name: `sign_up_method-google-user_id-${state.email}`,
          });
          if (data.pubtoolToken) {
            localStorage.setItem(AUTH_HEADERS.PUBTOOL_TOKEN, data.pubtoolToken);
            history.push(ROUTES.home);
          }
        })
        .catch(handleServerError)
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.SIGN_UP });
      return authModel
        .signUp({ ...state })
        .then(data => {
          setEmailToken(data.resendEmailToken);
          trackEvent({
            category: CATEGORY_NAMES.PUBTOOL_EVENTS,
            action: 'signup',
            name: `sign_up_method-email-user_id-${state.email}`,
          });
        })
        .catch(handleServerError)
        .finally(() => setLoading(false));
    }
  });

  const onSubmit = () => {
    authModel.isSpaceNameUsed(state.clientName).then(signUp).catch(handleServerError);
  };

  const handleChange = (item: Record<string, string | boolean>) =>
    setState(prevState => ({
      ...prevState,
      ...item,
    }));

  const disableSignUpButton =
    state.email === '' ||
    state.clientName === '' ||
    (!google && (state.password === '' || state.passwordConfirmation === ''));

  const onSignIn = () => {
    trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.SIGN_IN });
    redirectToSignIn();
  };

  const onSignUpWithGoogle = () => {
    trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.SIGN_UP_GOOGLE });
    authModel.redirectToGoogleAuth(authModel.googleFlow.signUp);
  };

  return (
    <BuilderAuthCard mobile={isMobile}>
      <Helmet title={t(google ? 'pages.auth.googleSignUpPage.tabTitle' : 'pages.auth.signUpPage.tabTitle')} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'start' : 'center',
          gap: '20px',
          flexGrow: 1,
          maxWidth: '440px',
        }}
      >
        {isMobile && (
          <a href="https://cozmos.world">
            <CozmosSVGWhite style={{ width: '120px', height: '32px' }} />
          </a>
        )}
        <div style={{ flexGrow: 1 }} />
        <BuilderTitle mobile={isMobile}>Join Cozmos</BuilderTitle>
        <BuilderSubtitle>Sign Up for free!</BuilderSubtitle>
        <div style={{ flexGrow: 0.2 }} />
        {!google && (
          <>
            <BuilderStyledButton
              fullWidth
              color="primary"
              text="Sign Up with Google"
              variant="outlined"
              Icon={GoogleIcon}
              onClick={onSignUpWithGoogle}
            />
            <Typography style={{ color: COLORS.white, alignSelf: 'center' }}>OR</Typography>
          </>
        )}
        <ValidatorForm onSubmit={onSubmit} style={{ width: '100%' }}>
          <Box mb={4}>
            <BuilderTextValidator
              autoFocus
              validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.isEmail]}
              label={t('common.forms.labels.email')}
              value={state.email}
              fieldName={authModel.fields.email}
              handleChange={handleChange}
              type="email"
              disabled={google}
              autoComplete="email"
              disableMinHeight
            />
          </Box>
          <Box display="flex" flexDirection="column" mb="20px" style={{ gap: '20px' }}>
            <BuilderTextValidator
              autoFocus
              validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.spaceName]}
              label={t('common.forms.labels.worldName')}
              value={state.clientName}
              fieldName={authModel.fields.space}
              handleChange={handleChange}
              InputProps={{
                autoCapitalize: 'none',
                endAdornment: (
                  <InputAdornment position="end" style={{ paddingRight: '14px' }}>
                    .cozmos.world
                  </InputAdornment>
                ),
              }}
              disableMinHeight
            />
          </Box>
          <Box display="flex" alignItems="center" flexDirection="row">
            <Checkbox
              style={{
                color: COLORS.builderBrandColor,
              }}
              checked={state.advertiseNotifications}
              onChange={() => handleChange({ advertiseNotifications: !state.advertiseNotifications })}
            />
            <CheckboxHint>I want to receive offers, news and updates about Cozmos.</CheckboxHint>
          </Box>

          <Box display="flex" flexDirection="column" mb="20px" style={{ gap: '20px' }}>
            {!google && (
              <>
                <BuilderTextValidator
                  autoFocus
                  validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.minPasswordLength(6)]}
                  label={t('pages.auth.createPassword.passwordInputLabel')}
                  value={state.password}
                  fieldName={authModel.fields.password}
                  handleChange={handleChange}
                  type="password"
                  disableMinHeight
                />
                <BuilderTextValidator
                  validators={[VALIDATORS_MAP.required]}
                  label="Repeat new password"
                  value={state.passwordConfirmation}
                  fieldName={authModel.fields.passwordConfirmation}
                  handleChange={handleChange}
                  type="password"
                  disableMinHeight
                />
              </>
            )}
            <BuilderStyledButton
              fullWidth
              color="primary"
              type="submit"
              text="Create Account"
              loading={loading}
              disabled={disableSignUpButton}
            />
          </Box>
        </ValidatorForm>
        <Typography style={{ color: COLORS.builderSubTitleColor, textAlign: 'center', width: '100%' }}>
          By clicking <b>Create Account</b>, you agree to Cozmos&apos;s
          <br />
          <Link
            href={LINKS.terms}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.builderSubTitleColor, textDecoration: 'underline' }}
          >
            {t('pages.auth.common.terms')}
          </Link>
          {` ${t('pages.auth.common.and')} `}
          <Link
            href={LINKS.privacyPolicy}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.builderSubTitleColor, textDecoration: 'underline' }}
          >
            {t('pages.auth.common.privacy')}
          </Link>
        </Typography>
        {!isMobile && (
          <div>
            <Typography display="inline" style={{ color: COLORS.builderSubTitleColor }}>
              Already have an account?&nbsp;
            </Typography>
            <BuilderFormLink text="Log In" variant="text" onClick={onSignIn} style={{ fontSize: '14px' }} />
          </div>
        )}
        <div style={{ flexGrow: 2 }} />
        {!google && <BuilderContactUsFooter />}
        <div style={{ flexGrow: 0.3 }} />
      </div>
    </BuilderAuthCard>
  );
};

export default BuilderSignUp;
